/**
 * Reply affordance for an inbound email — shared between the generic timeline
 * row (`EmailEvent`) and the email-thread conversation view.
 *
 * A thin renderer over `useEmailReply`, which owns the behavior (account
 * matching, CC exclusion, subject prefixing, provenance) — extracted so the
 * record header's action bar can drive the same reply without this button.
 * Behavior is pinned by `__tests__/EmailEvent.test.tsx`.
 *
 * Renders `null` when a valid reply can't be constructed (no thread id, no
 * sender address, no org/user context). Callers gate on direction themselves —
 * replying only makes sense on received messages.
 */

import type { ComponentProps } from 'react';

import { Button } from '@/lib/base/buttons/button';

import type { EmailPayload } from '../types';
import { useEmailReply } from './useEmailReply';

export interface EmailReplyButtonProps {
  /** The thread entity the email event belongs to (proposal source id). */
  entityId: string;
  /**
   * The timeline event id of the message being replied to — stamped into the
   * proposal's provenance (`event_ids`). See `useEmailReply`.
   */
  replyToEventId?: string;
  /** The email event's payload. */
  payload: EmailPayload | undefined;
  /**
   * Wrapper classes applied only when the button actually renders — lets
   * callers add spacing without leaving an empty spacer behind when the
   * reply gate fails.
   */
  className?: string;
  /**
   * Button color variant. Timeline rows keep the quiet 'tertiary' default;
   * the conversation footer uses 'secondary' for a real, bordered action.
   */
  color?: ComponentProps<typeof Button>['color'];
  /** Optional leading icon (e.g. CornerUpLeft for the conversation footer). */
  iconLeading?: ComponentProps<typeof Button>['iconLeading'];
}

export function EmailReplyButton({
  entityId,
  replyToEventId,
  payload,
  className,
  color = 'tertiary',
  iconLeading,
}: EmailReplyButtonProps) {
  const { canReply, label, subject, handleReply, isCreatingReply } =
    useEmailReply({ entityId, replyToEventId, payload });

  if (!canReply) {
    return null;
  }

  const button = (
    <Button
      size='sm'
      color={color}
      iconLeading={iconLeading}
      onClick={handleReply}
      isDisabled={isCreatingReply}
      isLoading={isCreatingReply}
      showTextWhileLoading
      aria-label={`${label} to email: ${subject}`}
    >
      {label}
    </Button>
  );

  return className ? <div className={className}>{button}</div> : button;
}
